import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { metricsAPI, dashboardsAPI } from '../services/api';
import AdminWidgetBuilder from './AdminWidgetBuilder';
import '../styles/DashboardSettings.css';

const DashboardSettings = () => {
  const navigate = useNavigate();
  const user = useStore((state) => state.user);
  const currentCompany = useStore((state) => state.currentCompany);
  const currentDashboard = useStore((state) => state.currentDashboard);
  const setCurrentDashboard = useStore((state) => state.setCurrentDashboard);
  const metricDefinitions = useStore((state) => state.metricDefinitions);
  const setMetricDefinitions = useStore((state) => state.setMetricDefinitions);
  const animationsEnabled = useStore((state) => state.animationsEnabled);
  const setAnimationsEnabled = useStore((state) => state.setAnimationsEnabled);

  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [currentWidgets, setCurrentWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showAdminBuilder, setShowAdminBuilder] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [showDashboardForm, setShowDashboardForm] = useState(false);
  const [editingDashboard, setEditingDashboard] = useState(null);
  const [dashboardFormData, setDashboardFormData] = useState({ name: '', isDefault: false, staleThresholdMinutes: 5 });

  // Helper functions for state management
  const clearError = () => setError('');
  const clearSuccess = () => setSuccessMessage('');
  const showSuccess = (message, timeout = 3000) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), timeout);
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  const dashboards = useStore((state) => state.dashboards);
  const setDashboards = useStore((state) => state.setDashboards);

  useEffect(() => {
    loadData();
  }, [currentDashboard]);

  const loadData = async () => {
    try {
      setLoading(true);
      clearError();

      // Load metric definitions for current company
      if (currentCompany && currentCompany.id) {
        const metricsResponse = await metricsAPI.getDefinitions(currentCompany.id);
        if (metricsResponse.success) {
          setMetricDefinitions(metricsResponse.data);
          setAvailableMetrics(metricsResponse.data);
        }
      } else {
        setAvailableMetrics(metricDefinitions);
      }

      // Load current widgets - always update even if empty to clear previous dashboard's widgets
      if (currentDashboard) {
        setCurrentWidgets(currentDashboard.widgets || []);
      } else {
        setCurrentWidgets([]);
      }
    } catch (error) {
      console.error('Error loading settings data:', error);
      setError('Failed to load dashboard settings');
    } finally {
      setLoading(false);
    }
  };

  const isWidgetAdded = (metricKey) => {
    return currentWidgets.some((widget) => widget.metric_key === metricKey);
  };

  const handleAddWidget = async (metric) => {
    try {
      clearError();
      clearSuccess();

      if (!currentDashboard) {
        setError('No dashboard selected');
        return;
      }

      // Save current scroll position
      const scrollPosition = window.scrollY;

      // Calculate position for new widget (next available spot)
      const maxY = currentWidgets.reduce((max, w) => {
        const pos = w.position || { y: 0, h: 3 };
        return Math.max(max, (pos.y || 0) + (pos.h || 3));
      }, 0);

      const position = {
        x: 0,
        y: maxY,
        w: 4,
        h: 3,
      };

      const response = await dashboardsAPI.addWidget(
        currentDashboard.id,
        metric.metric_key,
        position,
        {}
      );

      if (response.success) {
        showSuccess(`Added "${metric.display_name}" to dashboard`);

        // Reload dashboard to get updated widgets
        const dashboardResponse = await dashboardsAPI.getById(currentDashboard.id);
        if (dashboardResponse.success) {
          setCurrentDashboard(dashboardResponse.data);
          setCurrentWidgets(dashboardResponse.data.widgets);

          // Restore scroll position after state updates
          setTimeout(() => {
            window.scrollTo(0, scrollPosition);
          }, 0);
        }
      }
    } catch (error) {
      console.error('Error adding widget:', error);
      setError(error.response?.data?.error || 'Failed to add widget');
    }
  };

  const handleRemoveWidget = async (widget) => {
    try {
      clearError();
      clearSuccess();

      if (!currentDashboard) {
        setError('No dashboard selected');
        return;
      }

      if (window.confirm(`Remove "${widget.display_name}" from dashboard?`)) {
        // Save current scroll position
        const scrollPosition = window.scrollY;

        const response = await dashboardsAPI.deleteWidget(
          currentDashboard.id,
          widget.id
        );

        if (response.success) {
          showSuccess(`Removed "${widget.display_name}" from dashboard`);

          // Update local state
          setCurrentWidgets(currentWidgets.filter((w) => w.id !== widget.id));

          // Reload dashboard
          const dashboardResponse = await dashboardsAPI.getById(currentDashboard.id);
          if (dashboardResponse.success) {
            setCurrentDashboard(dashboardResponse.data);

            // Restore scroll position after state updates
            setTimeout(() => {
              window.scrollTo(0, scrollPosition);
            }, 0);
          }
        }
      }
    } catch (error) {
      console.error('Error removing widget:', error);
      setError(error.response?.data?.error || 'Failed to remove widget');
    }
  };

  const getWidgetTypeIcon = (widgetType) => {
    switch (widgetType) {
      case 'gauge':
        return '📊';
      case 'table':
        return '📋';
      case 'chart':
      case 'line_chart':
        return '📈';
      case 'area_chart':
        return '📉';
      case 'service_status':
        return '🔧';
      case 'count':
        return '🔢';
      default:
        return '📌';
    }
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  const handleWidgetCreated = async () => {
    setEditingMetric(null);
    // Reload metrics
    await loadData();
    showSuccess(editingMetric ? 'Widget updated successfully!' : 'Widget created successfully!');
  };

  const handleEditWidget = (metric) => {
    setEditingMetric(metric);
    setShowAdminBuilder(true);
  };

  const handleDeleteWidget = async (metric) => {
    try {
      clearError();

      // Check if any dashboards are using this widget
      const isInUse = currentWidgets.some((w) => w.metric_key === metric.metric_key);

      const confirmMessage = isInUse
        ? `"${metric.display_name}" is currently on your dashboard. Deleting it will remove it from all users' dashboards. Are you sure you want to delete this widget definition?`
        : `Are you sure you want to delete "${metric.display_name}"? This action cannot be undone.`;

      if (window.confirm(confirmMessage)) {
        const { default: adminAPI } = await import('../services/adminAPI');
        const response = await adminAPI.deleteMetricDefinition(metric.id);

        if (response.success) {
          // Reload data
          await loadData();
          showSuccess(`Deleted "${metric.display_name}" successfully`);
        }
      }
    } catch (error) {
      console.error('Error deleting widget:', error);
      setError(error.response?.data?.error || 'Failed to delete widget definition');
    }
  };

  const handleCloseBuilder = () => {
    setShowAdminBuilder(false);
    setEditingMetric(null);
  };

  const handleCreateDashboard = () => {
    setEditingDashboard(null);
    setDashboardFormData({ name: '', isDefault: false });
    setShowDashboardForm(true);
  };

  const handleEditDashboard = (dashboard) => {
    setEditingDashboard(dashboard);
    setDashboardFormData({
      name: dashboard.name,
      isDefault: dashboard.is_default,
      staleThresholdMinutes: dashboard.stale_threshold_minutes || 5
    });
    setShowDashboardForm(true);
  };

  const handleDashboardSubmit = async (e) => {
    e.preventDefault();
    clearError();

    if (!currentCompany) {
      setError('No company selected');
      return;
    }

    if (!dashboardFormData.name || dashboardFormData.name.length < 3) {
      setError('Dashboard name must be at least 3 characters');
      return;
    }

    try {
      if (editingDashboard) {
        // Update existing dashboard
        const response = await dashboardsAPI.update(editingDashboard.id, {
          name: dashboardFormData.name,
          isDefault: dashboardFormData.isDefault,
          staleThresholdMinutes: dashboardFormData.staleThresholdMinutes
        });

        if (response.success) {
          // Reload dashboards
          const dashboardsResponse = await dashboardsAPI.getAll();
          if (dashboardsResponse.success) {
            const companyDashboards = dashboardsResponse.data.filter(d => d.company_id === currentCompany.id);
            setDashboards(companyDashboards);
          }
          setShowDashboardForm(false);
          showSuccess('Dashboard updated successfully');
        }
      } else {
        // Create new dashboard
        const response = await dashboardsAPI.create(
          dashboardFormData.name,
          currentCompany.id,
          dashboardFormData.isDefault,
          dashboardFormData.staleThresholdMinutes
        );

        if (response.success) {
          // Reload dashboards
          const dashboardsResponse = await dashboardsAPI.getAll();
          if (dashboardsResponse.success) {
            const companyDashboards = dashboardsResponse.data.filter(d => d.company_id === currentCompany.id);
            setDashboards(companyDashboards);

            // Optionally switch to the new dashboard
            const fullDashboardResponse = await dashboardsAPI.getById(response.data.id);
            if (fullDashboardResponse.success) {
              setCurrentDashboard(fullDashboardResponse.data);
            }
          }
          setShowDashboardForm(false);
          showSuccess('Dashboard created successfully');
        }
      }
    } catch (error) {
      console.error('Error saving dashboard:', error);
      setError(error.response?.data?.error || 'Failed to save dashboard');
    }
  };

  const handleDeleteDashboard = async (dashboard) => {
    if (dashboards.length <= 1) {
      setError('Cannot delete the last dashboard for this company');
      return;
    }

    const confirmMessage = `Are you sure you want to delete "${dashboard.name}"?\n\nThis will remove all widgets from this dashboard. This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await dashboardsAPI.delete(dashboard.id);
      if (response.success) {
        // Reload dashboards
        const dashboardsResponse = await dashboardsAPI.getAll();
        if (dashboardsResponse.success) {
          const companyDashboards = dashboardsResponse.data.filter(d => d.company_id === currentCompany.id);
          setDashboards(companyDashboards);

          // If we deleted the current dashboard, switch to another one
          if (currentDashboard?.id === dashboard.id && companyDashboards.length > 0) {
            const newDashboard = companyDashboards.find(d => d.is_default) || companyDashboards[0];
            const fullDashboardResponse = await dashboardsAPI.getById(newDashboard.id);
            if (fullDashboardResponse.success) {
              setCurrentDashboard(fullDashboardResponse.data);
            }
          }
        }

        showSuccess('Dashboard deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      setError(error.response?.data?.error || 'Failed to delete dashboard');
    }
  };

  // Handle modal close only if click starts and ends on overlay
  const handleModalOverlayMouseDown = (e) => {
    if (e.target.className === 'modal-overlay') {
      e.target.dataset.mousedownOnOverlay = 'true';
    }
  };

  const handleModalOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay' && e.target.dataset.mousedownOnOverlay === 'true') {
      setShowDashboardForm(false);
    }
    delete e.target.dataset.mousedownOnOverlay;
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h2>Dashboard Settings</h2>
        <button onClick={() => navigate('/')} className="btn-secondary">
          Back to Dashboard
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {showAdminBuilder && (
        <AdminWidgetBuilder
          onClose={handleCloseBuilder}
          onSuccess={handleWidgetCreated}
          editingMetric={editingMetric}
        />
      )}

      <div className="settings-content">
        {/* Dashboard Management */}
        {isAdmin && currentCompany && (
          <div className="settings-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0 }}>Dashboards for {currentCompany.name}</h3>
                <p style={{ color: '#b0b0b0', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {dashboards.length} dashboard{dashboards.length !== 1 ? 's' : ''} available
                </p>
              </div>
              <button onClick={handleCreateDashboard} className="btn-primary-small">
                + Create New Dashboard
              </button>
            </div>

            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {dashboards.map((dashboard) => (
                <div
                  key={dashboard.id}
                  style={{
                    padding: '1rem',
                    backgroundColor: currentDashboard?.id === dashboard.id ? 'rgba(74, 158, 255, 0.1)' : 'var(--bg-secondary)',
                    border: `1px solid ${currentDashboard?.id === dashboard.id ? 'var(--accent-blue)' : 'var(--border-color)'}`,
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <strong style={{ color: '#ffffff' }}>{dashboard.name}</strong>
                      {dashboard.is_default && (
                        <span style={{
                          fontSize: '0.7rem',
                          padding: '0.15rem 0.4rem',
                          borderRadius: '3px',
                          backgroundColor: 'rgba(74, 158, 255, 0.2)',
                          color: '#4a9eff',
                          fontWeight: '600'
                        }}>
                          DEFAULT
                        </span>
                      )}
                      {currentDashboard?.id === dashboard.id && (
                        <span style={{
                          fontSize: '0.7rem',
                          padding: '0.15rem 0.4rem',
                          borderRadius: '3px',
                          backgroundColor: 'rgba(76, 175, 80, 0.2)',
                          color: '#4caf50',
                          fontWeight: '600'
                        }}>
                          CURRENT
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#b0b0b0' }}>
                      Created {new Date(dashboard.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleEditDashboard(dashboard)}
                      className="btn-secondary-small"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteDashboard(dashboard)}
                      className="btn-danger-small"
                      disabled={dashboards.length <= 1}
                      title={dashboards.length <= 1 ? 'Cannot delete the last dashboard' : 'Delete dashboard'}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dashboard Form Modal */}
        {showDashboardForm && (
          <div className="modal-overlay" onMouseDown={handleModalOverlayMouseDown} onClick={handleModalOverlayClick}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingDashboard ? 'Edit Dashboard' : 'Create New Dashboard'}</h2>
                <button onClick={() => setShowDashboardForm(false)} className="modal-close">&times;</button>
              </div>

              <form onSubmit={handleDashboardSubmit}>
                <div className="form-group">
                  <label>Dashboard Name *</label>
                  <input
                    type="text"
                    value={dashboardFormData.name}
                    onChange={(e) => setDashboardFormData({ ...dashboardFormData, name: e.target.value })}
                    placeholder="e.g., Sales Dashboard, Operations View"
                    required
                    minLength={3}
                  />
                </div>

                <div className="form-group">
                  <label>Stale Threshold (minutes) *</label>
                  <input
                    type="number"
                    value={dashboardFormData.staleThresholdMinutes}
                    onChange={(e) => setDashboardFormData({ ...dashboardFormData, staleThresholdMinutes: parseInt(e.target.value) || 5 })}
                    placeholder="5"
                    required
                    min={1}
                    max={60}
                  />
                  <small>Dashboard status will show as "Stale" if no updates are received for this many minutes</small>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={dashboardFormData.isDefault}
                      onChange={(e) => setDashboardFormData({ ...dashboardFormData, isDefault: e.target.checked })}
                      style={{ width: 'auto' }}
                    />
                    Set as default dashboard
                  </label>
                  <small>The default dashboard will be loaded when users first select this company</small>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => setShowDashboardForm(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingDashboard ? 'Update Dashboard' : 'Create Dashboard'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* User Preferences */}
        <div className="settings-section">
          <h3 style={{ marginBottom: '1rem' }}>Preferences</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '1rem' }}>
              <input
                type="checkbox"
                checked={animationsEnabled}
                onChange={(e) => setAnimationsEnabled(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span>Enable widget animations</span>
            </label>
            <small style={{ color: '#b0b0b0', marginLeft: 'auto' }}>
              When enabled, widgets will animate when values change
            </small>
          </div>
        </div>
        {/* All Widgets - Unified List (Admin Only) */}
        {isAdmin && (
          <div className="settings-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div>
                <h3 style={{ margin: 0 }}>All Widgets ({availableMetrics.length})</h3>
                <p style={{ color: '#b0b0b0', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {currentWidgets.length} currently on your dashboard
                </p>
              </div>
              <button onClick={() => setShowAdminBuilder(true)} className="btn-primary-small">
                + Create New Widget
              </button>
            </div>

          {availableMetrics.length === 0 ? (
            <p className="empty-state">
              No widgets available. {isAdmin ? 'Create your first widget to get started.' : 'Contact your administrator to add widgets.'}
            </p>
          ) : (
            <div className="widget-list">
              {availableMetrics.map((metric) => {
                const onDashboard = isWidgetAdded(metric.metric_key);
                const dashboardWidget = currentWidgets.find(w => w.metric_key === metric.metric_key);

                return (
                  <div
                    key={metric.id}
                    className={`widget-item ${onDashboard ? 'on-dashboard' : ''}`}
                  >
                    <div className="widget-item-icon">
                      {getWidgetTypeIcon(metric.widget_type)}
                    </div>
                    <div className="widget-item-info">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div className="widget-item-name">{metric.display_name}</div>
                        {onDashboard && (
                          <span className="badge-on-dashboard">● On Dashboard</span>
                        )}
                      </div>
                      <div className="widget-item-description">{metric.description}</div>
                      <div className="widget-item-type">
                        Type: {metric.widget_type}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => handleEditWidget(metric)}
                            className="btn-secondary-small"
                            title="Edit widget definition"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteWidget(metric)}
                            className="btn-danger-small"
                            title="Delete widget definition"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {onDashboard ? (
                        <button
                          onClick={() => handleRemoveWidget(dashboardWidget)}
                          className="btn-remove-small"
                        >
                          Remove from Dashboard
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAddWidget(metric)}
                          className="btn-primary-small"
                        >
                          Add to Dashboard
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardSettings;
